import { useState } from 'react';
import { Loader2, Tag, X, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn, formatPrice } from '@/lib/utils';

interface CouponValidationResult {
  valid: boolean;
  discountAmount: number;
  discountPercent: number;
  errorMessage?: string;
}

interface CouponInputProps {
  onApply: (code: string) => Promise<CouponValidationResult>;
  onRemove: () => void;
  appliedCode?: string | null;
  appliedDiscount?: number;
  appliedPercent?: number;
  className?: string;
}

export function CouponInput({
  onApply,
  onRemove,
  appliedCode,
  appliedDiscount,
  appliedPercent,
  className,
}: CouponInputProps) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);

    try {
      const result = await onApply(trimmed);
      if (result.valid) {
        setCode('');
        setError(null);
      } else {
        setError(result.errorMessage || 'קוד קופון לא תקין');
      }
    } catch {
      setError('שגיאה באימות הקופון');
    } finally {
      setLoading(false);
    }
  };

  // Success state – coupon is applied
  if (appliedCode && appliedDiscount && appliedDiscount > 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-between gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950/30',
          className,
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Check className="h-4 w-4 shrink-0 text-emerald-600" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300 truncate">
              קופון: {appliedCode}
            </p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400">
              {appliedPercent ? `${appliedPercent}% הנחה` : ''} • -{formatPrice(appliedDiscount)}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-emerald-600 hover:text-destructive hover:bg-destructive/10"
          onClick={onRemove}
          aria-label="הסר קופון"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  // Input state
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Tag className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleApply();
              }
            }}
            placeholder="קוד קופון"
            className={cn(
              'pr-9 rounded-2xl text-sm',
              error && 'border-destructive focus-visible:ring-destructive',
            )}
            disabled={loading}
            dir="ltr"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-2xl px-4 shrink-0"
          onClick={handleApply}
          disabled={loading || !code.trim()}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'החל'}
        </Button>
      </div>
      {error && <p className="text-xs text-destructive pr-1">{error}</p>}
    </div>
  );
}
